import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { brandingApi, type Branding } from '../lib/branding';
import { useAuth } from '../hooks/useAuth';
import { Plus, Palette, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

function resolveLogoSrc(publicLogoPath: string | null): string | null {
  if (!publicLogoPath) return null;
  if (publicLogoPath.startsWith('http://') || publicLogoPath.startsWith('https://')) {
    return publicLogoPath;
  }
  return publicLogoPath;
}

export default function BrandingPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const canManage = currentUser?.role === 'super_user';

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Branding | null>(null);
  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Branding | null>(null);

  const [uiAlert, setUiAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({ open: false, title: 'Notice', message: '' });

  const { data: items, isLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: () => brandingApi.getAll(),
    enabled: canManage,
  });

  const isEditing = editingItem !== null;

  const resetForm = () => {
    setEditingItem(null);
    setName('');
    setLogoFile(null);
    setLogoPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleOpenCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleOpenEdit = (row: Branding) => {
    setEditingItem(row);
    setName(row.name);
    setLogoFile(null);
    setLogoPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return resolveLogoSrc(row.publicLogoPath);
    });
    setFormOpen(true);
  };

  const handleRowClick = (row: Branding, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    handleOpenEdit(row);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setLogoFile(file);
    setLogoPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : resolveLogoSrc(editingItem?.publicLogoPath ?? null);
    });
  };

  const showError = (error: unknown, fallback: string) => {
    const err = error as {
      response?: { data?: { error?: string; details?: unknown } };
      message?: string;
    };
    const base = err.response?.data?.error || err.message || fallback;
    const details = err.response?.data?.details;
    const detailStr =
      details != null
        ? `\n\n${typeof details === 'string' ? details : JSON.stringify(details, null, 2)}`
        : '';
    setUiAlert({ open: true, title: 'Error', message: `${base}${detailStr}`.trim() });
  };

  const createMutation = useMutation({
    mutationFn: (payload: { name: string; logoFile?: File | null }) => brandingApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setFormOpen(false);
      resetForm();
    },
    onError: (error) => showError(error, 'Failed to create branding'),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: string; name: string; logoFile?: File | null }) =>
      brandingApi.update(payload.id, { name: payload.name, logoFile: payload.logoFile }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setFormOpen(false);
      resetForm();
    },
    onError: (error) => showError(error, 'Failed to update branding'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => brandingApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    },
    onError: (error) => showError(error, 'Failed to delete branding'),
  });

  const handleSubmitForm = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setUiAlert({ open: true, title: 'Validation', message: 'Please enter a name for this branding.' });
      return;
    }
    if (isEditing && editingItem) {
      updateMutation.mutate({ id: editingItem.id, name: trimmed, logoFile });
    } else {
      createMutation.mutate({ name: trimmed, logoFile });
    }
  };

  const handleDeleteClick = (row: Branding, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      deleteMutation.mutate(itemToDelete.id);
    }
  };

  const formPending = createMutation.isPending || updateMutation.isPending;

  if (!canManage) {
    return (
      <div className="px-2 py-3 sm:px-0">
        <p className="text-muted-foreground">You do not have permission to manage branding.</p>
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <>
      <div className="px-2 py-3 sm:px-0">
        <div className="flex flex-row items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex-shrink-0">
            <h1 className="text-2xl sm:text-3xl font-bold font-heading">Branding</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Logos and labels used across organisations and sites. Click a row to edit.
            </p>
          </div>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Branding</span>
          </Button>
        </div>

        {!items || items.length === 0 ? (
          <Card className="text-center py-12">
            <Palette className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No branding yet. Add one to get started.</p>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[88px]">Logo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell text-muted-foreground">Updated</TableHead>
                    <TableHead className="text-right w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row: Branding) => (
                    <TableRow
                      key={row.id}
                      onClick={(e) => handleRowClick(row, e)}
                      className="cursor-pointer hover:bg-muted/50"
                    >
                      <TableCell>
                        {resolveLogoSrc(row.publicLogoPath) ? (
                          <img
                            src={resolveLogoSrc(row.publicLogoPath)!}
                            alt=""
                            className="h-12 w-12 rounded-md object-contain border border-border bg-muted"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-md border border-dashed border-muted-foreground/40 flex items-center justify-center text-xs text-muted-foreground">
                            —
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                        {new Date(row.updatedAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteClick(row, e)}
                          disabled={deleteMutation.isPending}
                          className="text-destructive hover:text-destructive"
                          title="Delete branding"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="hidden sm:inline ml-1">Delete</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      <Dialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit branding' : 'Add branding'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="branding-name">Name</Label>
              <Input
                id="branding-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Acme Corp theme"
                autoComplete="off"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="branding-logo">
                Logo image {isEditing ? '(leave empty to keep current)' : '(optional)'}
              </Label>
              <Input
                id="branding-logo"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/avif,image/heic,image/heif,.heic,.heif"
                onChange={handleLogoChange}
              />
              {logoPreviewUrl && (
                <div className="mt-2 flex items-center gap-3">
                  <img
                    src={logoPreviewUrl}
                    alt="Preview"
                    className="h-16 w-16 rounded-md object-contain border border-border bg-muted"
                  />
                  <span className="text-sm text-muted-foreground">Preview</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmitForm} disabled={formPending}>
              {formPending ? (isEditing ? 'Saving…' : 'Creating…') : isEditing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete branding?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete
                ? `This will permanently delete "${itemToDelete.name}". Organisations and sites linked to this branding will lose the link.`
                : 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={uiAlert.open} onOpenChange={(open) => setUiAlert((prev) => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-wrap">{uiAlert.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
